import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  appendAlineUrlParams,
  defaultWorkflowFileSort,
  getWorkflowRoute,
  GQL,
  IntakeFormDefinitionSchema,
  IntakeFormValuesSchema,
  validateIntakeFormValues,
} from '@aline/common';
import { link, routes } from '@aline/routes';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { ConfirmationDialog, ConfirmationDialogVariant } from 'src/components/generic';
import { getLaunchIntegrationIssues } from 'src/components/send-for-signature/components/AddIntegration';
import { COMPLETE_REQUIRED_INTAKE_QUESTIONS_MSG } from 'src/components/workflow/workflow-detail/intake-form/intake-form-validation-messages';
import { Apollo } from 'src/gql/generated';
import { useCreateWorkflowInstance } from 'src/hooks';
import { useTabContext } from 'src/layouts/tabs/tab-context';
import { makeStyles } from 'tss-react/mui';

import { CreatingDocumentsDialog } from './components/CreatingDocumentsDialog';
import { templateQuickStartSteps } from './components/helpers';
import {
  getLaunchDocumentFields,
  getLaunchTemplateConnectionsToShow,
  getLaunchTemplatePageTitle,
  getLaunchWarningConnections,
} from './components/launch-template-visibility';
import type {
  LaunchTemplateWorkflow,
  TemplateQuickStartState,
} from './components/LaunchTemplateBody';
import { LaunchTemplateBody } from './components/LaunchTemplateBody';
import { getLaunchTemplatePrimaryDisabledReason } from './launch-template-disabled-reason';
import { PageHeader } from './PageHeader';

const INTEGRATION_SERVICES = {
  salesforce: 'salesforce',
} as const;

type IntegrationService = (typeof INTEGRATION_SERVICES)[keyof typeof INTEGRATION_SERVICES];

interface IntegrationParams {
  hasSalesforceParams: boolean;
  initialIntegrationRecordMap: Record<string, string>;
  objectType: GQL.SalesforceObjectType | null;
  salesforceRecordId: string | null;
  service: IntegrationService | null;
}

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
  },
}));

export function LaunchTemplateQuickStart() {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { updateActiveTabTitle } = useTabContext();
  const templateQuickStartMatch = useMatch({
    end: false,
    path: routes.QuickStartLaunchTemplateSelectedTemplate,
  });
  const [showRequiredErrorsToken, setShowRequiredErrorsToken] = useState(0);
  const [showIntegrationIssuesConfirmation, setShowIntegrationIssuesConfirmation] = useState(false);
  const templateWorkflowId = templateQuickStartMatch?.params?.templateWorkflowId ?? null;
  const [activeStep, setActiveStep] = useState<number>(!templateWorkflowId ? 1 : 2);
  const [showTemplateSelectionConfirmation, setShowTemplateSelectionConfirmation] = useState(false);
  const { hasSalesforceParams, initialIntegrationRecordMap, objectType, salesforceRecordId } =
    useIntegrationParams();

  // Fetch Salesforce record data when params and template are present
  const { data: salesforceData, loading: salesforceLoading } =
    Apollo.useGetSalesforceRecordDataQuery({
      skip: !hasSalesforceParams || !templateWorkflowId,
      variables: {
        input: {
          objectType: objectType ?? GQL.SalesforceObjectType.Opportunity,
          salesforceRecordId: salesforceRecordId ?? '',
          templateId: templateWorkflowId ?? '',
        },
      },
    });
  const salesforceRecordName = salesforceData?.salesforceRecordData.recordName ?? null;
  const preloadedOpportunityNames = useMemo(
    () => (salesforceRecordName ? { [GQL.Integration.SalesForce]: salesforceRecordName } : {}),
    [salesforceRecordName]
  );

  const {
    data: launchTemplateData,
    error: launchTemplateError,
    loading: launchTemplateLoading,
    refetch: refetchLaunchTemplateData,
  } = Apollo.useLaunchTemplateDataQuery({
    // A cached template can predate edits made elsewhere, so always revalidate it.
    fetchPolicy: 'cache-and-network',
    skip: !templateWorkflowId,
    variables: { workflowId: templateWorkflowId ?? '' },
  });
  /**
   * Tabs hide instead of unmounting and re-run their effects when shown again, so this refetches
   * a template edited in another tab. A new template id is already being fetched by the query.
   */
  const fetchedTemplateIdRef = useRef<UUID | null>(null);
  useEffect(() => {
    if (templateWorkflowId && fetchedTemplateIdRef.current === templateWorkflowId)
      void refetchLaunchTemplateData();
    fetchedTemplateIdRef.current = templateWorkflowId;
  }, [refetchLaunchTemplateData, templateWorkflowId]);
  const selectedTemplateWorkflow = useMemo(() => {
    const workflow = launchTemplateData?.workflow;
    if (!workflow) return null;

    return {
      ...workflow,
      files: [...workflow.files].sort(defaultWorkflowFileSort),
    };
  }, [launchTemplateData?.workflow]);
  const allConnections = launchTemplateData?.apiDeckConnections ?? [];
  const isTemplatePublished = Boolean(selectedTemplateWorkflow?.publishedAt);
  const mappedCrmIntegrations = useMemo(
    () => new Set(selectedTemplateWorkflow?.mappedCrmIntegrations ?? []),
    [selectedTemplateWorkflow?.mappedCrmIntegrations]
  );
  // A background refetch keeps the form on screen, whether it is loading or fails.
  const loadingTemplateData = Boolean(
    templateWorkflowId && ((launchTemplateLoading && !launchTemplateData) || salesforceLoading)
  );
  const templateDataError = Boolean(launchTemplateError && !launchTemplateData);

  const isTemplateValid =
    !templateWorkflowId || loadingTemplateData || templateDataError || selectedTemplateWorkflow;
  const effectiveActiveStep = isTemplateValid ? activeStep : 1;

  // Auto-publish template when coming from Salesforce and template is not published
  const [togglePublishTemplate] = Apollo.useTogglePublishTemplateMutation();
  useEffect(() => {
    if (hasSalesforceParams && selectedTemplateWorkflow && !isTemplatePublished) {
      togglePublishTemplate({ variables: { workflowId: selectedTemplateWorkflow.id } });
    }
  }, [hasSalesforceParams, selectedTemplateWorkflow, isTemplatePublished, togglePublishTemplate]);

  const {
    createWorkflowFromTemplate,
    openCreatingDocuments,
    resetTemplateQuickStartState,
    setShowCancelConfirmation,
    setTemplateQuickStartState,
    showCancelConfirmation,
    templateQuickStartState,
  } = useLaunchWorkflowFromTemplate(initialIntegrationRecordMap);
  const connectionsToShow = getLaunchTemplateConnectionsToShow({
    integrationRecordMap: templateQuickStartState.integrationRecordMap,
    mappedCrmIntegrations,
    userConnections: allConnections,
  });
  const launchIntegrationIssues = getLaunchIntegrationIssues({
    connections: getLaunchWarningConnections({ connectionsToShow, mappedCrmIntegrations }),
    integrationRecordMap: templateQuickStartState.integrationRecordMap,
  });
  const shouldValidateIntakeForm = Boolean(templateWorkflowId && effectiveActiveStep !== 1);

  // Gate only the final launch action on the selected template's required intake-form fields.
  const intakeFormTemplate = selectedTemplateWorkflow?.intakeFormTemplate ?? null;
  const intakeIncomplete = useMemo(() => {
    /**
     * Block launch until the selected template's intake form has loaded — until
     * then we don't yet know whether a required intake exists, so enabling
     * launch could surface a server error for missing required fields.
     */
    if (!shouldValidateIntakeForm) return false;
    if (loadingTemplateData || templateDataError) return true;
    if (!intakeFormTemplate) return false;

    const parsed = IntakeFormDefinitionSchema.safeParse(intakeFormTemplate.formDefinition);
    /**
     * Fail closed: an unparseable definition blocks launch rather than slipping
     * past the gate. Mirror the server, which rejects both missing required
     * fields and any provided-but-malformed value (validateIntakeFormValues).
     */
    if (!parsed.success) return true;
    /**
     * Seed validation with the template's defaultValues (launcher values take
     * precedence), mirroring the server's launch merge — otherwise a required
     * field the author pre-filled would read as missing and block launch.
     */
    const defaults = IntakeFormValuesSchema.safeParse(intakeFormTemplate.defaultValues);
    const result = validateIntakeFormValues(parsed.data, {
      ...(defaults.success ? defaults.data : {}),
      ...(templateQuickStartState.intakeFormValues ?? {}),
    });

    return Boolean(result.fieldErrors) || !result.isComplete;
  }, [
    shouldValidateIntakeForm,
    loadingTemplateData,
    templateDataError,
    intakeFormTemplate,
    templateQuickStartState.intakeFormValues,
  ]);
  // Pre-populate workflow name with Salesforce record name when available
  useEffect(() => {
    if (!salesforceRecordName) return;

    const updates: Partial<TemplateQuickStartState> = {};

    if (!templateQuickStartState.workflowName) {
      const templateName = salesforceData?.salesforceRecordData.template?.name ?? '';
      updates.workflowName = templateName
        ? `${templateName} - ${salesforceRecordName}`
        : salesforceRecordName;
    }

    if (Object.keys(updates).length) {
      setTemplateQuickStartState(prev => ({ ...prev, ...updates }));
    }
  }, [
    salesforceRecordName,
    salesforceData?.salesforceRecordData.template?.name,
    templateQuickStartState.workflowName,
    setTemplateQuickStartState,
  ]);
  const isWorkflowUntitled =
    effectiveActiveStep === 2 && templateQuickStartState.workflowName === '';
  const pageTitle = getLaunchTemplatePageTitle(selectedTemplateWorkflow?.name);

  useEffect(() => {
    updateActiveTabTitle(templateQuickStartState.workflowName || 'Launch Template');
  }, [templateQuickStartState.workflowName, updateActiveTabTitle]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <div className={classes.root}>
        <PageHeader
          activeStep={effectiveActiveStep}
          back={() => setShowTemplateSelectionConfirmation(true)}
          backDisabledReason={
            effectiveActiveStep === 1 ? 'Move to the next step before going back' : ''
          }
          next={async () => {
            switch (effectiveActiveStep) {
              case 1: {
                if (!templateWorkflowId) return;

                return setActiveStep(2);
              }
              default: {
                if (!selectedTemplateWorkflow || loadingTemplateData || templateDataError) return;
                if (intakeIncomplete) {
                  enqueueSnackbar(COMPLETE_REQUIRED_INTAKE_QUESTIONS_MSG, { variant: 'warning' });
                  setShowRequiredErrorsToken(token => token + 1);
                  return;
                }
                if (launchIntegrationIssues.length) {
                  setShowIntegrationIssuesConfirmation(true);
                  return;
                }

                return createWorkflowFromTemplate(selectedTemplateWorkflow);
              }
            }
          }}
          pageTitle={pageTitle}
          primaryDisabledReason={getLaunchTemplatePrimaryDisabledReason({
            isTemplatePublished,
            isWorkflowUntitled,
            loadingTemplateData,
            templateDataError,
            templateWorkflowId,
          })}
          steps={templateQuickStartSteps}
          onClose={() => setShowCancelConfirmation(true)}
        />
        <LaunchTemplateBody
          activeStep={effectiveActiveStep}
          connectionsToShow={connectionsToShow}
          loadError={templateDataError}
          loading={loadingTemplateData}
          preloadedOpportunityNames={preloadedOpportunityNames}
          quickStartState={templateQuickStartState}
          showRequiredErrorsToken={showRequiredErrorsToken}
          templateWorkflow={selectedTemplateWorkflow}
          templateWorkflowId={templateWorkflowId ?? null}
          onRetry={refetchLaunchTemplateData}
          onValuesChange={values => {
            setTemplateQuickStartState(prev => ({
              ...prev,
              ...(typeof values === 'function' ? values(prev) : values),
            }));
          }}
        />
      </div>
      <CreatingDocumentsDialog open={openCreatingDocuments} />
      <ConfirmationDialog
        message="Integration fields from unlinked or disconnected integrations may stay empty."
        open={showIntegrationIssuesConfirmation}
        primaryText="Continue"
        secondaryText="Go back"
        title="Launch with integration issues?"
        variant={
          launchIntegrationIssues.includes('error')
            ? ConfirmationDialogVariant.Error
            : ConfirmationDialogVariant.Warn
        }
        onClickPrimary={() => {
          if (!selectedTemplateWorkflow) return;
          setShowIntegrationIssuesConfirmation(false);
          void createWorkflowFromTemplate(selectedTemplateWorkflow);
        }}
        onClose={() => setShowIntegrationIssuesConfirmation(false)}
      />
      <ConfirmationDialog
        message="Any completed values on the launch form will be cleared."
        open={showTemplateSelectionConfirmation}
        primaryText="Go Back"
        secondaryText="Cancel"
        title="Select a new template?"
        onClickPrimary={() => {
          resetTemplateQuickStartState();
          setActiveStep(1);
          setShowTemplateSelectionConfirmation(false);
        }}
        onClose={() => setShowTemplateSelectionConfirmation(false)}
      />
      <ConfirmationDialog
        message="Are you sure you want to cancel?"
        open={showCancelConfirmation}
        primaryText="Yes"
        secondaryText="No"
        title="Cancel template launch?"
        onClickPrimary={() => navigate(link.Home())}
        onClose={() => setShowCancelConfirmation(false)}
      />
    </>
  );
}

function parseIntegrationService(param: string | null): IntegrationService | null {
  if (param === INTEGRATION_SERVICES.salesforce) return INTEGRATION_SERVICES.salesforce;
  return null;
}

function parseSalesforceParams(searchParams: URLSearchParams) {
  const oppId = searchParams.get('oppId');
  const accountId = searchParams.get('accountId');

  const recordId = oppId ?? accountId;
  const objectType = oppId
    ? GQL.SalesforceObjectType.Opportunity
    : accountId
      ? GQL.SalesforceObjectType.Account
      : null;

  return { objectType, recordId };
}

function useIntegrationParams(): IntegrationParams {
  const [searchParams] = useSearchParams();

  const service = parseIntegrationService(searchParams.get('service'));
  const { objectType, recordId: salesforceRecordId } = parseSalesforceParams(searchParams);

  const hasSalesforceParams = Boolean(salesforceRecordId && objectType);

  const initialIntegrationRecordMap = useMemo((): Record<string, string> => {
    if (!salesforceRecordId) return {};
    return { [GQL.Integration.SalesForce]: salesforceRecordId };
  }, [salesforceRecordId]);

  return {
    hasSalesforceParams,
    initialIntegrationRecordMap,
    objectType,
    salesforceRecordId,
    service,
  };
}

function createInitialTemplateQuickStartState(
  initialIntegrationRecordMap: Record<string, string>
): TemplateQuickStartState {
  return {
    alineFieldValues: [],
    excludedDocumentIds: [],
    integrationRecordMap: initialIntegrationRecordMap,
    moreDocumentIds: [],
    parties: [],
    replacementFiles: [],
    roleAssignmentMap: {},
    templateIdToContractName: {},
    workflowName: '',
  };
}

function useLaunchWorkflowFromTemplate(initialIntegrationRecordMap: Record<string, string> = {}) {
  const navigate = useNavigate();
  const { createWorkflowInstance } = useCreateWorkflowInstance();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [openCreatingDocuments, setOpenCreatingDocuments] = useState<boolean>(false);
  const [templateQuickStartState, setTemplateQuickStartState] = useState<TemplateQuickStartState>(
    () => createInitialTemplateQuickStartState(initialIntegrationRecordMap)
  );
  const resetTemplateQuickStartState = useCallback(() => {
    const initialState = createInitialTemplateQuickStartState(initialIntegrationRecordMap);
    setTemplateQuickStartState(initialState);
  }, [initialIntegrationRecordMap]);
  const createWorkflowFromTemplate = useCallback(
    async (templateWorkflow: LaunchTemplateWorkflow) => {
      setOpenCreatingDocuments(true);

      try {
        // Values typed into a field before its document was removed would fail the launch.
        const launchFieldIds = new Set(
          getLaunchDocumentFields(templateWorkflow, templateQuickStartState).map(
            field => field.fieldId
          )
        );
        const { data } = await createWorkflowInstance({
          ...templateQuickStartState,
          alineFieldValues: templateQuickStartState.alineFieldValues?.filter(value =>
            launchFieldIds.has(value.fieldId)
          ),
          templateWorkflowId: templateWorkflow.id,
        });
        if (!data) return;

        const {
          createWorkflowInstance: { workflow },
        } = data;
        const firstDocument = workflow?.files.at(0);
        if (firstDocument) {
          const route = appendAlineUrlParams(
            link.DocumentDetailFields({ documentId: firstDocument.document.id }),
            {
              templateQuickStartParams: { quickStartOn: true },
            }
          );
          navigate(route);
        } else {
          navigate(getWorkflowRoute(workflow.id));
        }
      } finally {
        /**
         * Always dismiss the "creating documents" dialog, even if the launch
         * mutation throws — otherwise it stays open and blocks the page.
         */
        setOpenCreatingDocuments(false);
      }
    },
    [createWorkflowInstance, navigate, templateQuickStartState]
  );

  return {
    createWorkflowFromTemplate,
    openCreatingDocuments,
    resetTemplateQuickStartState,
    setOpenCreatingDocuments,
    setShowCancelConfirmation,
    setTemplateQuickStartState,
    showCancelConfirmation,
    templateQuickStartState,
  };
}
